import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { Shell } from "./components/Shell";
import { Spinner } from "./components/ui";
import { canManageAnything, isAdmin, managedGroupIds, useMe } from "./lib/session";
import { AuditPage } from "./pages/admin/audit";
import { BrandingPage } from "./pages/admin/branding";
import { DiagnosticsPage } from "./pages/admin/diagnostics";
import { GatewaysPage } from "./pages/admin/gateways";
import { HierarchyPage } from "./pages/admin/hierarchy";
import { SourcesPage } from "./pages/admin/sources";
import { UsersPage } from "./pages/admin/users";
import { ChangeTemporaryPasswordPage, ForgotPasswordPage, LoginPage, MfaSetupPage, SetPasswordPage } from "./pages/auth";
import { AlarmsPage } from "./pages/alarms";
import { AnalysisListPage, AnalysisPage } from "./pages/analysis";
import { ChartsPage } from "./pages/charts";
import { HomePage } from "./pages/home";
import { OscillographyListPage, OscillographyPage, OscillographyTrashPage } from "./pages/oscillography";
import { SoeFilePage, SoePage, SoeTrashPage } from "./pages/soe";
import { RegulatorPage } from "./pages/regulator";
import { ProfilePage } from "./pages/profile";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/** Exige login (e o 2FA em dia, para o admin da plataforma). */
function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  // Senha temporária antes do 2FA: a API recusa tudo o mais até a troca.
  if (me.mustChangePassword) return <Navigate to="/senha/trocar" replace />;
  if (me.mfaSetupRequired) return <Navigate to="/2fa" replace />;
  return <>{children}</>;
}

/** Esconde telas de administração de quem não tem o papel (a API recusa de qualquer jeito). */
function Guard({ allow, children }: { allow: (me: ReturnType<typeof useMe>["data"]) => boolean; children: ReactNode }) {
  const { data: me } = useMe();
  return allow(me) ? <>{children}</> : <Navigate to="/" replace />;
}

const brandManager = (me: ReturnType<typeof useMe>["data"]) => isAdmin(me) || managedGroupIds(me).length > 0;

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/senha/esqueci" element={<ForgotPasswordPage />} />
          <Route path="/senha/definir" element={<SetPasswordPage mode="invite" />} />
          <Route path="/senha/redefinir" element={<SetPasswordPage mode="reset" />} />
          <Route path="/senha/trocar" element={<ChangeTemporaryPasswordPage />} />
          <Route path="/2fa" element={<MfaSetupPage />} />
          <Route
            element={
              <RequireAuth>
                <Shell />
              </RequireAuth>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="perfil" element={<ProfilePage />} />
            <Route path="reguladores/:id" element={<RegulatorPage />} />
            <Route path="alarmes" element={<AlarmsPage />} />
            <Route path="graficos" element={<ChartsPage />} />
            <Route path="oscilografias" element={<OscillographyListPage />} />
            <Route path="oscilografias/lixeira" element={<Guard allow={canManageAnything}><OscillographyTrashPage /></Guard>} />
            <Route path="oscilografias/:id" element={<OscillographyPage />} />
            <Route path="eventos" element={<SoePage />} />
            <Route path="eventos/lixeira" element={<Guard allow={canManageAnything}><SoeTrashPage /></Guard>} />
            <Route path="eventos/:id" element={<SoeFilePage />} />
            <Route path="analises" element={<AnalysisListPage />} />
            <Route path="analises/:id" element={<AnalysisPage />} />
            <Route path="admin/hierarquia" element={<Guard allow={canManageAnything}><HierarchyPage /></Guard>} />
            <Route path="admin/usuarios" element={<Guard allow={canManageAnything}><UsersPage /></Guard>} />
            <Route path="admin/marca" element={<Guard allow={brandManager}><BrandingPage /></Guard>} />
            <Route path="admin/auditoria" element={<Guard allow={brandManager}><AuditPage /></Guard>} />
            <Route path="admin/fontes" element={<Guard allow={isAdmin}><SourcesPage /></Guard>} />
            <Route path="admin/gateways" element={<Guard allow={isAdmin}><GatewaysPage /></Guard>} />
            <Route path="admin/diagnostico" element={<Guard allow={isAdmin}><DiagnosticsPage /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
